import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Flex,
  FlexRow,
  Col,
  Heading,
  Glyph,
  Message,
  Pill,
  Body,
} from '@edreamsodigeo/prisma-design-system';

import { useTranslation } from '@edreamsodigeo/frontend-core';

import { colors } from './constants/design';
import useDisplayTypeBySite from 'clientState/hooks/useDisplayTypeBySite/useDisplayTypeBySite';

const TextMessage = ({
  children,
  isFooter = false,
  weight = 'normal',
  ...params
}) => (
  <Message type="persuasive">
    {isFooter ? (
      <Body weight={weight} type="xSmall" {...params}>
        {children}
      </Body>
    ) : (
      <Heading weight={weight} type="6" {...params}>
        {children}
      </Heading>
    )}
  </Message>
);

const MessageLower = ({ dealBuyPath, isFooter }) => {
  const { t } = useTranslation();
  return (
    <Box ml={-1} py={1} width="100%">
      <Box display="inline-block" mr={2}>
        <TextMessage weight="bold">
          {t(`results.reprice.lower.title.${dealBuyPath}`)}
        </TextMessage>
      </Box>
      <Box display="inline-block">
        <TextMessage isFooter={isFooter}>
          {t(`results.reprice.lower.description.${dealBuyPath}`)}
        </TextMessage>
      </Box>
    </Box>
  );
};

const MessageHigher = ({ dealBuyPath, isFooter }) => {
  const { t } = useTranslation();
  return (
    <Box ml={-2} width="100%">
      <Box display="inline-block" mr={2}>
        <TextMessage weight="bold" isFooter={isFooter}>
          {t(`results.reprice.higher.title.${dealBuyPath}`)}
        </TextMessage>
      </Box>
      <Box display="inline-block">
        <TextMessage isFooter={isFooter}>
          {t(`results.reprice.higher.description.${dealBuyPath}`)}
        </TextMessage>
      </Box>
    </Box>
  );
};

const RepriceDefault = ({ isDecreasing, dealBuyPath }) => {
  const MessageDefault = isDecreasing ? MessageLower : MessageHigher;
  return (
    <Pill
      rounded={true}
      type="neutral"
      padding={0}
      sx={{
        background: `linear-gradient(90deg, ${colors.reprice.backgroundGradient} 28.13%, ${colors.reprice.backgroundGradientStop} 100%)`,
        width: '100%',
      }}
    >
      <FlexRow alignItems={'center'} gap={0}>
        <Col>
          <IconRotated isDecreasing={isDecreasing} />
        </Col>
        <Col width="100%">
          <MessageDefault dealBuyPath={dealBuyPath} />
        </Col>
      </FlexRow>
    </Pill>
  );
};

const IconRotated = ({ isDecreasing }) => {
  const { isUKDisplayType } = useDisplayTypeBySite();
  const color = !isUKDisplayType() ? 'neutral.0' : 'red.50';
  const position = isDecreasing
    ? {
        transform: `rotate(-44deg)`,
        position: 'relative',
        left: '-8px',
      }
    : {
        transform: `rotate(-137deg)`,
        position: 'relative',
        top: '0',
        left: '-11px',
      };
  return (
    <Glyph
      category="Deals"
      name="Lightning"
      color={color}
      size={['medium', 'large']}
      sx={position}
    />
  );
};

const TextMessageHeader = ({ children, weight = 'normal', ...params }) => {
  return (
    <Box>
      <Heading weight={weight} type="6" {...params}>
        {children}
      </Heading>
    </Box>
  );
};

const MessageHeader = ({ isDecreasing, dealBuyPath }) => {
  const { t } = useTranslation();
  const lowerOrHigher = isDecreasing ? 'lower' : 'higher';
  return (
    <Box ml={-2}>
      <FlexRow gap={1} to="right">
        <Col>
          <TextMessageHeader weight="bold">
            {t(`results.reprice.${lowerOrHigher}.title.${dealBuyPath}`)}
          </TextMessageHeader>
        </Col>
        <Col>
          <TextMessageHeader>
            {t(`results.reprice.${lowerOrHigher}.description.${dealBuyPath}`)}
          </TextMessageHeader>
        </Col>
      </FlexRow>
    </Box>
  );
};

const RepriceHeader = ({ isDecreasing, dealBuyPath }) => {
  return (
    <Pill
      rounded={true}
      sx={{
        padding: 0,
        width: '100%',
        background: 'transparent',
      }}
    >
      <FlexRow alignItems={'center'}>
        <Col>
          <IconRotated isDecreasing={isDecreasing} />
        </Col>
        <Col>
          <MessageHeader
            isDecreasing={isDecreasing}
            dealBuyPath={dealBuyPath}
          />
        </Col>
      </FlexRow>
    </Pill>
  );
};

const RepriceFooter = ({ trend, dealBuyPath }) => {
  const isDecreasing = trend === 'down';
  const MessageDefault = isDecreasing ? MessageLower : MessageHigher;

  return (
    <Box
      sx={{
        background: `white`,
        width: 'calc(100% - 4px)',
        margin: '-5px auto 0',
        position: 'relative',
        top: '-2px',
        zIndex: 0,
        borderBottomRightRadius: '6px',
        borderBottomLeftRadius: '6px',
      }}
    >
      <Box
        pb={2}
        sx={{
          background: `linear-gradient(90deg,rgba(249,112,12,0.2) 28.13%,rgba(218,56,53,0.2) 100%)`,
          width: '100%',
        }}
      >
        <Flex alignItems={'center'} pl={5} pt={3}>
          <IconRotated isDecreasing={isDecreasing} />
          <MessageDefault dealBuyPath={dealBuyPath} isFooter={true} />
        </Flex>
      </Box>
    </Box>
  );
};

const Reprice = ({ trend, dealBuyPath, isFooter = false }) => {
  const { isUKDisplayType } = useDisplayTypeBySite();
  if (trend === 'equal') return null;
  if (isFooter)
    return <RepriceFooter trend={trend} dealBuyPath={dealBuyPath} />;
  return isUKDisplayType() ? (
    <RepriceDefault isDecreasing={trend === 'down'} dealBuyPath={dealBuyPath} />
  ) : (
    <RepriceHeader isDecreasing={trend === 'down'} dealBuyPath={dealBuyPath} />
  );
};

Reprice.propTypes = {
  trend: PropTypes.oneOf(['equal', 'up', 'down']).isRequired,
  dealBuyPath: PropTypes.oneOf(['deal.weekend', 'deal.hottest']).isRequired,
  isFooter: PropTypes.bool,
};

export default Reprice;
